import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ProfileIntelReport, type ReportData } from './ProfileIntelReport'

// ── Types ─────────────────────────────────────────────────────────────────────

type Platform = 'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'reddit'
type TargetType =
  | 'page' | 'group' | 'post' | 'reel' | 'event' | 'reels' | 'reviews'  // facebook
  | 'profile' | 'stories' | 'media'                                        // instagram
  | 'user' | 'video' | 'search'                                            // tiktok / youtube
  | 'channel' | 'playlist'                                                 // youtube
  | 'subreddit'                                                             // reddit

interface ManualMode {
  platform: Platform
  targetType: TargetType
  identifier: string
  extra?: Record<string, string>
}

interface FetchState {
  status: 'idle' | 'loading' | 'success' | 'error'
  data: any
  error: string | null
  resolvedTarget: { platform: string; target_type: string; identifier: string } | null
}

// ── Constants ─────────────────────────────────────────────────────────────────

const PLATFORM_META: Record<Platform, { label: string; color: string; bg: string; border: string; icon: string }> = {
  facebook:  { label: 'Facebook',  color: '#3b82f6', bg: 'bg-blue-950/30',   border: 'border-blue-800/40',   icon: 'f' },
  instagram: { label: 'Instagram', color: '#ec4899', bg: 'bg-pink-950/30',   border: 'border-pink-800/40',   icon: 'ig' },
  tiktok:    { label: 'TikTok',    color: '#14b8a6', bg: 'bg-teal-950/30',   border: 'border-teal-800/40',   icon: 'tt' },
  youtube:   { label: 'YouTube',   color: '#ef4444', bg: 'bg-red-950/30',    border: 'border-red-800/40',    icon: 'yt' },
  reddit:    { label: 'Reddit',    color: '#f97316', bg: 'bg-orange-950/30', border: 'border-orange-800/40', icon: 'r' },
}

const PLATFORM_TARGETS: Record<Platform, Array<{ type: TargetType; label: string; placeholder: string; paramKey: string }>> = {
  facebook: [
    { type: 'page',     label: 'Page',         placeholder: 'https://facebook.com/YourPage',       paramKey: 'url' },
    { type: 'group',    label: 'Group',        placeholder: 'https://facebook.com/groups/...',     paramKey: 'url' },
    { type: 'post',     label: 'Post / Reel',  placeholder: 'https://facebook.com/.../posts/...',  paramKey: 'url' },
    { type: 'event',    label: 'Event',        placeholder: 'https://facebook.com/events/...',     paramKey: 'url' },
    { type: 'reels',    label: 'Profile Reels', placeholder: 'https://facebook.com/YourPage',      paramKey: 'url' },
    { type: 'reviews',  label: 'Reviews',      placeholder: 'https://facebook.com/YourCompany',   paramKey: 'url' },
    { type: 'search',   label: 'Search',       placeholder: 'Query: cars',                        paramKey: 'q' },
  ],
  instagram: [
    { type: 'profile',  label: 'Profile',      placeholder: 'username (no @)',                    paramKey: 'username' },
    { type: 'media',    label: 'Post / Reel',  placeholder: 'Post URL or shortcode',              paramKey: 'url' },
    { type: 'stories',  label: 'Stories',      placeholder: 'username (no @)',                    paramKey: 'username' },
    { type: 'search',   label: 'Search',       placeholder: 'Query: sunset',                     paramKey: 'q' },
  ],
  tiktok: [
    { type: 'user',     label: 'User Profile', placeholder: 'TikTok numeric user_id',             paramKey: 'user_id' },
    { type: 'video',    label: 'Video',        placeholder: 'https://tiktok.com/@.../video/...',  paramKey: 'url' },
    { type: 'search',   label: 'Search',       placeholder: 'Query: fyp dance',                  paramKey: 'q' },
  ],
  youtube: [
    { type: 'channel',  label: 'Channel',      placeholder: 'UCxxxxx... or channel URL',         paramKey: 'id' },
    { type: 'video',    label: 'Video',        placeholder: 'https://youtube.com/watch?v=...',   paramKey: 'url' },
    { type: 'playlist', label: 'Playlist',     placeholder: 'Playlist ID',                       paramKey: 'id' },
    { type: 'search',   label: 'Search',       placeholder: 'Query: tutorial react',             paramKey: 'q' },
  ],
  reddit: [
    { type: 'subreddit', label: 'Subreddit',   placeholder: 'wallstreetbets (no r/ needed)',     paramKey: 'name' },
    { type: 'user',      label: 'User',        placeholder: 'username',                          paramKey: 'username' },
    { type: 'post',      label: 'Post',        placeholder: 'https://reddit.com/r/.../comments/...', paramKey: 'url' },
    { type: 'search',    label: 'Search',      placeholder: 'Query: investing',                  paramKey: 'q' },
  ],
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getToken() { return localStorage.getItem('auth_token') || '' }


function fmt(n: any): string {
  const num = Number(n)
  if (isNaN(num)) return String(n ?? '—')
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`
  if (num >= 1_000)     return `${(num / 1_000).toFixed(1)}K`
  return String(num)
}

// ── Metric chip ───────────────────────────────────────────────────────────────

function Metric({ label, value, color = 'text-white' }: { label: string; value: any; color?: string }) {
  return (
    <div className="flex flex-col items-center bg-black/40 border border-gray-800/50 rounded-xl px-4 py-3 min-w-[80px]">
      <span className={`font-mono text-xl font-black ${color}`}>{fmt(value)}</span>
      <span className="font-mono text-[9px] text-gray-600 tracking-widest mt-0.5">{label}</span>
    </div>
  )
}

// ── Platform badge ────────────────────────────────────────────────────────────

function PlatformBadge({ platform }: { platform: string }) {
  const meta = PLATFORM_META[platform as Platform]
  if (!meta) return null
  return (
    <span className={`font-mono text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-wide ${meta.bg} ${meta.border}`}
      style={{ color: meta.color }}>
      {meta.label}
    </span>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border border-gray-800/50 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-900/30 hover:bg-gray-900/50 transition-colors"
      >
        <span className="font-mono text-xs font-bold text-gray-300 tracking-wider">{title}</span>
        <span className="text-gray-600 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Post card ─────────────────────────────────────────────────────────────────

function PostCard({ post }: { post: any }) {
  const text = post.message || post.story || post.text || post.caption ||
    post.description || post.title || post.name || post.selftext || post.body || ''
  const date = post.created_time || post.create_time || post.date || post.timestamp || post.created_utc || ''
  const img = post.full_picture || post.thumbnail || post.thumbnail_url ||
    post.cover || post.ai_dynamic_cover ||
    post.attachments?.[0]?.url || post.preview?.images?.[0]?.source?.url || ''
  const likes = post.likes?.summary?.total_count ?? post.like_count ?? post.digg_count ?? post.score ?? post.statistics?.likeCount ?? 0
  const comments = post.comments?.summary?.total_count ?? post.comment_count ?? post.num_comments ?? post.statistics?.commentCount ?? 0
  const views = post.video_view_count ?? post.play_count ?? post.view_count ?? post.statistics?.viewCount ?? 0
  const url = post.permalink_url || post.url || post.permalink || post.shortUrl || ''

  return (
    <div className="bg-black/30 border border-gray-800/40 rounded-xl overflow-hidden hover:border-gray-700/60 transition-colors">
      {img && (
        <img src={img} alt="" className="w-full h-36 object-cover opacity-80"
          onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
      )}
      <div className="p-3 space-y-2">
        {text && <p className="text-gray-300 text-xs leading-relaxed line-clamp-3">{text}</p>}
        <div className="flex items-center gap-3 text-gray-600 font-mono text-[10px]">
          {likes > 0 && <span>♥ {fmt(likes)}</span>}
          {comments > 0 && <span>💬 {fmt(comments)}</span>}
          {views > 0 && <span>👁 {fmt(views)}</span>}
          {date && <span className="ml-auto">{String(date).slice(0, 10)}</span>}
        </div>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="block text-blue-500 hover:text-blue-300 font-mono text-[10px] truncate">
            {url}
          </a>
        )}
      </div>
    </div>
  )
}

// ── Profile header card ───────────────────────────────────────────────────────

function ProfileHeader({ data, platform = '' }: { data: any; platform?: string }) {
  const name = data.name || data.username || data.title || data.display_name ||
    data.full_name || data.subreddit || data.snippet?.title || ''
  const bio = data.about || data.description || data.biography || data.public_description ||
    data.snippet?.description || ''
  const avatar = data.profile_picture || data.picture || data.avatar_url ||
    data.profile_image_url || data.thumbnails?.high?.url ||
    data.snippet?.thumbnails?.high?.url || ''
  const cover = data.cover_photo?.source || data.cover?.source || ''
  const followers = data.followers_count || data.follower_count || data.fan_count ||
    data.subscriberCount || data.subscribers || data.statistics?.subscriberCount || 0
  const following = data.follows_count || data.following_count || 0
  const posts = data.media_count || data.post_count || data.videoCount ||
    data.statistics?.videoCount || 0
  const verified = data.verified || data.is_verified || data.blue_badge || false
  const created = data.founded || data.created_at || data.publishedAt ||
    data.snippet?.publishedAt || data.created_utc || ''
  const url = data.link || data.external_url || data.website || ''
  const category = data.category || data.subreddit_type || data.category_name || ''

  return (
    <div className="rounded-xl overflow-hidden border border-gray-800/50">
      {/* Cover */}
      {cover && (
        <div className="w-full h-32 bg-gray-900 overflow-hidden">
          <img src={cover} alt="" className="w-full h-full object-cover opacity-80"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          {avatar ? (
            <img src={avatar} alt="" className="w-14 h-14 rounded-full border-2 border-gray-700 object-cover shrink-0"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center text-gray-600 text-xl font-bold shrink-0">
              {(name || '?')[0].toUpperCase()}
            </div>
          )}
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-white font-bold text-base font-mono truncate">{name || '—'}</h2>
              {verified && <span className="text-blue-400 text-xs">✓</span>}
              <PlatformBadge platform={platform} />
              {category && (
                <span className="font-mono text-[9px] text-gray-500 bg-gray-800/50 border border-gray-700/40 px-2 py-0.5 rounded">
                  {category}
                </span>
              )}
            </div>
            {bio && <p className="text-gray-400 text-xs mt-1.5 leading-relaxed line-clamp-2">{bio}</p>}
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-300 font-mono text-[10px] mt-1 block truncate">
                {url}
              </a>
            )}
            {created && (
              <div className="text-gray-600 font-mono text-[10px] mt-1">
                Created: {String(created).slice(0, 10)}
              </div>
            )}
          </div>
        </div>

        {/* Metrics */}
        <div className="flex flex-wrap gap-2 mt-4">
          {followers > 0  && <Metric label="FOLLOWERS" value={followers} color="text-green-400" />}
          {following > 0  && <Metric label="FOLLOWING" value={following} />}
          {posts > 0      && <Metric label="POSTS/VIDEOS" value={posts} />}
        </div>
      </div>
    </div>
  )
}

// ── Comments section ──────────────────────────────────────────────────────────

function CommentsSection({ comments }: { comments: any[] }) {
  if (!comments?.length) return null
  return (
    <div className="space-y-2 max-h-72 overflow-y-auto">
      {comments.slice(0, 30).map((c: any, i) => {
        const author = c.from?.name || c.author || c.username || c.user?.username || c.commenter_name || '?'
        const text = c.message || c.body || c.text || c.comment_text || ''
        const likes = c.like_count || c.likes?.summary?.total_count || c.ups || 0
        const date = c.created_time || c.date || c.created_utc || ''
        return (
          <div key={i} className="flex items-start gap-3 px-3 py-2.5 bg-black/30 border border-gray-800/30 rounded-lg">
            <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 text-xs font-bold shrink-0">
              {(author || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-mono text-xs font-bold">{author}</span>
                {likes > 0 && <span className="text-gray-600 font-mono text-[10px]">♥ {fmt(likes)}</span>}
                {date && <span className="text-gray-700 font-mono text-[10px] ml-auto">{String(date).slice(0, 10)}</span>}
              </div>
              <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{text}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Platform-specific result renderers ────────────────────────────────────────

function FacebookResult({ data }: { data: any }) {
  const details = data.details || data.data || {}
  const posts: any[]   = Array.isArray(data.posts?.data || data.posts)
    ? (data.posts?.data || data.posts) : []
  const reels: any[]   = Array.isArray(data.reels?.data || data.reels)
    ? (data.reels?.data || data.reels) : []
  const videos: any[]  = Array.isArray(data.videos?.data || data.videos)
    ? (data.videos?.data || data.videos) : []
  const comments: any[] = Array.isArray(data.comments?.data || data.comments)
    ? (data.comments?.data || data.comments) : []

  return (
    <div className="space-y-4">
      {details && Object.keys(details).length > 0 && !details.error && (
        <ProfileHeader data={details} />
      )}
      {posts.length > 0 && (
        <Section title={`POSTS (${posts.length})`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {posts.slice(0, 12).map((p, i) => <PostCard key={i} post={p} />)}
          </div>
        </Section>
      )}
      {reels.length > 0 && (
        <Section title={`REELS (${reels.length})`} defaultOpen={false}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {reels.slice(0, 10).map((r, i) => <PostCard key={i} post={r} />)}
          </div>
        </Section>
      )}
      {videos.length > 0 && (
        <Section title={`VIDEOS (${videos.length})`} defaultOpen={false}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {videos.slice(0, 10).map((v, i) => <PostCard key={i} post={v} />)}
          </div>
        </Section>
      )}
      {comments.length > 0 && (
        <Section title={`COMMENTS (${comments.length})`} defaultOpen={false}>
          <CommentsSection comments={comments} />
        </Section>
      )}
    </div>
  )
}

function InstagramResult({ data }: { data: any }) {
  const profile = data.profile || data.data || {}
  const posts: any[] = Array.isArray(data.posts?.data || data.posts)
    ? (data.posts?.data || data.posts) : []
  const highlights: any[] = Array.isArray(data.highlights?.data || data.highlights)
    ? (data.highlights?.data || data.highlights) : []
  const comments: any[] = Array.isArray(data.comments?.data || data.comments)
    ? (data.comments?.data || data.comments) : []
  const details = data.details || {}

  return (
    <div className="space-y-4">
      {profile && Object.keys(profile).length > 0 && !profile.error && (
        <ProfileHeader data={profile} />
      )}
      {details && Object.keys(details).length > 0 && !details.error && (
        <Section title="MEDIA DETAILS">
          <pre className="text-gray-400 font-mono text-[10px] whitespace-pre-wrap overflow-auto max-h-64">
            {JSON.stringify(details, null, 2)}
          </pre>
        </Section>
      )}
      {posts.length > 0 && (
        <Section title={`POSTS (${posts.length})`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {posts.slice(0, 12).map((p, i) => <PostCard key={i} post={p} />)}
          </div>
        </Section>
      )}
      {highlights.length > 0 && (
        <Section title={`HIGHLIGHTS (${highlights.length})`} defaultOpen={false}>
          <div className="flex flex-wrap gap-3">
            {highlights.slice(0, 20).map((h: any, i) => (
              <div key={i} className="flex flex-col items-center gap-1 w-16">
                {h.cover_media?.cropped_image_version?.url && (
                  <img src={h.cover_media.cropped_image_version.url} alt=""
                    className="w-14 h-14 rounded-full object-cover border-2 border-pink-700/50"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                )}
                <span className="font-mono text-[9px] text-gray-400 text-center truncate w-full">{h.title || ''}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
      {comments.length > 0 && (
        <Section title={`COMMENTS (${comments.length})`} defaultOpen={false}>
          <CommentsSection comments={comments} />
        </Section>
      )}
    </div>
  )
}

function TikTokResult({ data }: { data: any }) {
  const profile = data.profile?.userInfo?.user || data.profile?.data?.user || data.profile || {}
  const stats   = data.profile?.userInfo?.stats || data.profile?.data?.stats || data.profile?.stats || {}
  const rawPosts = data.posts?.itemList || data.posts?.data?.videos || data.posts
  const posts: any[] = Array.isArray(rawPosts) ? rawPosts : []
  const comments: any[] = Array.isArray(data.comments?.comments || data.comments)
    ? (data.comments?.comments || data.comments) : []
  const details = data.details?.itemInfo?.itemStruct || data.details || {}

  const merged = {
    ...profile,
    followers_count: stats.followerCount || 0,
    follows_count:   stats.followingCount || 0,
    media_count:     stats.videoCount || 0,
    heart_count:     stats.heartCount || 0,
  }

  return (
    <div className="space-y-4">
      {profile && Object.keys(profile).length > 0 && (
        <div className="space-y-4">
          <ProfileHeader data={merged} />
          {stats.heartCount > 0 && (
            <div className="flex flex-wrap gap-2">
              <Metric label="HEARTS" value={stats.heartCount} color="text-red-400" />
              <Metric label="VIDEOS" value={stats.videoCount} />
              <Metric label="DIGG COUNT" value={stats.diggCount || 0} />
            </div>
          )}
        </div>
      )}
      {details && details.id && (
        <Section title="VIDEO DETAILS">
          <div className="grid grid-cols-2 gap-3">
            {details.video?.cover && (
              <img src={details.video.cover} alt="" className="rounded-lg object-cover w-full h-48"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
            )}
            <div className="space-y-2 font-mono text-xs">
              {details.desc && <p className="text-gray-300">{details.desc}</p>}
              <div className="space-y-1 text-gray-500">
                <div>Views: <span className="text-white">{fmt(details.stats?.playCount)}</span></div>
                <div>Likes: <span className="text-white">{fmt(details.stats?.diggCount)}</span></div>
                <div>Comments: <span className="text-white">{fmt(details.stats?.commentCount)}</span></div>
                <div>Shares: <span className="text-white">{fmt(details.stats?.shareCount)}</span></div>
              </div>
            </div>
          </div>
        </Section>
      )}
      {posts.length > 0 && (
        <Section title={`POSTS (${posts.length})`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {posts.slice(0, 12).map((p, i) => <PostCard key={i} post={p} />)}
          </div>
        </Section>
      )}
      {comments.length > 0 && (
        <Section title={`COMMENTS (${comments.length})`} defaultOpen={false}>
          <CommentsSection comments={comments} />
        </Section>
      )}
    </div>
  )
}

function YouTubeResult({ data }: { data: any }) {
  const channelItems = data.details?.items || []
  const channel = channelItems[0] || {}
  const snippet = channel.snippet || {}
  const stats   = channel.statistics || {}
  const videoItems: any[] = data.videos?.items || data.results?.items || []
  const comments: any[] = data.comments?.items || []
  const captions: any[] = data.captions?.items || []
  const videoDetails = (data.details?.items || [])[0] || {}

  const profileData = {
    name:             snippet.title || videoDetails.snippet?.title || '',
    biography:        snippet.description || videoDetails.snippet?.description || '',
    profile_picture:  snippet.thumbnails?.high?.url || videoDetails.snippet?.thumbnails?.high?.url || '',
    created_at:       snippet.publishedAt || videoDetails.snippet?.publishedAt || '',
    followers_count:  stats.subscriberCount || 0,
    media_count:      stats.videoCount || 0,
    viewCount:        stats.viewCount || videoDetails.statistics?.viewCount || 0,
  }

  return (
    <div className="space-y-4">
      {(profileData.name || profileData.followers_count > 0) && (
        <ProfileHeader data={profileData} platform="youtube" />
      )}
      {profileData.viewCount > 0 && (
        <div className="flex flex-wrap gap-2">
          <Metric label="TOTAL VIEWS" value={profileData.viewCount} color="text-red-400" />
          {stats.videoCount > 0 && <Metric label="VIDEOS" value={stats.videoCount} />}
          {videoDetails.statistics?.likeCount > 0 && <Metric label="LIKES" value={videoDetails.statistics.likeCount} color="text-green-400" />}
          {videoDetails.statistics?.commentCount > 0 && <Metric label="COMMENTS" value={videoDetails.statistics.commentCount} />}
        </div>
      )}
      {videoItems.length > 0 && (
        <Section title={`VIDEOS (${videoItems.length})`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {videoItems.slice(0, 12).map((v, i) => {
              const id = v.id?.videoId || v.id
              return (
                <div key={i} className="bg-black/30 border border-gray-800/40 rounded-xl overflow-hidden hover:border-gray-700/50 transition-colors">
                  {v.snippet?.thumbnails?.medium?.url && (
                    <img src={v.snippet.thumbnails.medium.url} alt="" className="w-full h-36 object-cover" />
                  )}
                  <div className="p-3 space-y-1">
                    <p className="text-white font-mono text-xs font-bold line-clamp-2">{v.snippet?.title}</p>
                    <p className="text-gray-500 text-[10px]">{v.snippet?.channelTitle} · {String(v.snippet?.publishedAt || '').slice(0, 10)}</p>
                    {id && (
                      <a href={`https://youtube.com/watch?v=${id}`} target="_blank" rel="noopener noreferrer"
                        className="text-red-500 hover:text-red-300 font-mono text-[10px]">Watch →</a>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Section>
      )}
      {comments.length > 0 && (
        <Section title={`COMMENTS (${comments.length})`} defaultOpen={false}>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {comments.slice(0, 30).map((c: any, i) => {
              const top = c.snippet?.topLevelComment?.snippet || {}
              return (
                <div key={i} className="flex items-start gap-3 px-3 py-2.5 bg-black/30 border border-gray-800/30 rounded-lg">
                  {top.authorProfileImageUrl && (
                    <img src={top.authorProfileImageUrl} alt="" className="w-7 h-7 rounded-full shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-mono text-xs font-bold">{top.authorDisplayName}</span>
                      <span className="text-gray-600 font-mono text-[10px]">♥ {fmt(top.likeCount || 0)}</span>
                    </div>
                    <p className="text-gray-400 text-xs mt-0.5">{top.textDisplay}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </Section>
      )}
      {captions.length > 0 && (
        <Section title={`CAPTIONS / SUBTITLES (${captions.length})`} defaultOpen={false}>
          <div className="space-y-1">
            {captions.map((cap: any, i) => (
              <div key={i} className="flex items-center gap-3 text-xs font-mono text-gray-400 px-2 py-1.5 bg-black/30 rounded">
                <span className="text-gray-600">{cap.snippet?.language}</span>
                <span>{cap.snippet?.name?.simpleText || 'Auto-generated'}</span>
                <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded border ${cap.snippet?.trackKind === 'asr' ? 'text-yellow-600 border-yellow-800/40' : 'text-green-600 border-green-800/40'}`}>
                  {cap.snippet?.trackKind}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}

function RedditResult({ data }: { data: any }) {
  const info    = data.info?.data || data.info || {}
  const stats   = data.stats?.data || data.stats || {}
  const posts: any[] = Array.isArray(data.hot_posts?.data?.children || data.posts?.data?.children)
    ? (data.hot_posts?.data?.children || data.posts?.data?.children).map((c: any) => c.data || c)
    : []
  const comments: any[] = Array.isArray(data.comments?.data?.children || data.comments)
    ? (data.comments?.data?.children || data.comments).map((c: any) => c.data || c)
    : []
  const postData = data.data?.data?.children?.[0]?.data || data.data || {}

  const profileData = {
    name:            info.display_name || info.name || stats.name || postData.author || '',
    biography:       info.public_description || info.subreddit?.public_description || '',
    followers_count: info.subscribers || stats.link_karma || 0,
    media_count:     info.active_user_count || stats.comment_karma || 0,
    created_at:      info.created_utc ? new Date(info.created_utc * 1000).toISOString() : '',
    subreddit_type:  info.subreddit_type || '',
    profile_picture: info.icon_img || stats.icon_img || '',
  }

  return (
    <div className="space-y-4">
      {profileData.name && <ProfileHeader data={profileData} platform="reddit" />}

      {/* Karma stats for users */}
      {stats.link_karma != null && (
        <div className="flex flex-wrap gap-2">
          <Metric label="LINK KARMA" value={stats.link_karma} color="text-orange-400" />
          <Metric label="COMMENT KARMA" value={stats.comment_karma} />
          {stats.total_karma != null && <Metric label="TOTAL KARMA" value={stats.total_karma} />}
        </div>
      )}

      {/* Single post */}
      {postData.title && (
        <Section title="POST">
          <div className="space-y-3">
            <h3 className="text-white font-bold text-sm">{postData.title}</h3>
            {postData.selftext && <p className="text-gray-400 text-xs leading-relaxed">{postData.selftext}</p>}
            <div className="flex gap-4 text-gray-600 font-mono text-[10px]">
              <span>↑ {fmt(postData.score)}</span>
              <span>💬 {fmt(postData.num_comments)}</span>
              {postData.author && <span>by u/{postData.author}</span>}
            </div>
          </div>
        </Section>
      )}

      {posts.length > 0 && (
        <Section title={`HOT POSTS (${posts.length})`}>
          <div className="space-y-2">
            {posts.slice(0, 15).map((p: any, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2.5 bg-black/30 border border-gray-800/30 rounded-lg hover:border-gray-700/50 transition-colors">
                <div className="flex flex-col items-center gap-0.5 shrink-0 w-10">
                  <span className="text-orange-400 font-mono text-xs font-bold">{fmt(p.score || 0)}</span>
                  <span className="text-gray-700 font-mono text-[9px]">pts</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-200 text-xs font-mono font-bold line-clamp-2">{p.title}</p>
                  <div className="flex items-center gap-2 mt-1 text-gray-600 font-mono text-[9px]">
                    <span>💬 {fmt(p.num_comments)}</span>
                    {p.author && <span>u/{p.author}</span>}
                    {p.subreddit && <span>r/{p.subreddit}</span>}
                  </div>
                </div>
                {p.url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer"
                    className="text-orange-500 hover:text-orange-300 font-mono text-[10px] shrink-0">→</a>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {comments.length > 0 && (
        <Section title={`COMMENTS (${comments.length})`} defaultOpen={false}>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {comments.slice(0, 20).map((c: any, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2.5 bg-black/30 border border-gray-800/30 rounded-lg">
                <div className="flex flex-col items-center shrink-0 w-10">
                  <span className="text-orange-400 font-mono text-xs">{fmt(c.score)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-blue-400 font-mono text-[10px]">u/{c.author}</span>
                  <p className="text-gray-400 text-xs mt-0.5 leading-relaxed line-clamp-3">{c.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}

// ── Search results renderer ───────────────────────────────────────────────────

function SearchResults({ data }: { data: any }) {
  const items: any[] = Array.isArray(data.results?.data || data.results?.items || data.results)
    ? (data.results?.data || data.results?.items || data.results) : []

  return (
    <div className="space-y-2">
      <div className="text-gray-600 font-mono text-xs mb-2">{items.length} results</div>
      {items.slice(0, 20).map((item: any, i) => {
        const name = item.name || item.title || item.username || item.display_name || item.snippet?.title || ''
        const desc = item.about || item.description || item.bio || item.snippet?.description || ''
        const url  = item.link || item.url || item.permalink_url || ''
        const img  = item.picture || item.profile_pic || item.thumbnails?.medium?.url || item.snippet?.thumbnails?.medium?.url || ''
        const fans = item.fan_count || item.followers_count || item.statistics?.subscriberCount || 0
        return (
          <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-black/30 border border-gray-800/30 rounded-xl hover:border-gray-700/50 transition-colors">
            {img ? (
              <img src={img} alt="" className="w-9 h-9 rounded-full object-cover shrink-0"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-600 text-xs font-bold shrink-0">
                {(name || '?')[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-white font-mono text-xs font-bold truncate">{name}</div>
              {desc && <p className="text-gray-500 text-[10px] line-clamp-1">{desc}</p>}
            </div>
            {fans > 0 && <span className="text-gray-500 font-mono text-[10px] shrink-0">{fmt(fans)} followers</span>}
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-300 font-mono text-[10px] shrink-0">→</a>
            )}
          </div>
        )
      })}
      {items.length === 0 && (
        <div className="text-gray-700 font-mono text-xs text-center py-8 border border-dashed border-gray-800 rounded-xl">
          No results found
        </div>
      )}
    </div>
  )
}

// ── Raw JSON fallback ─────────────────────────────────────────────────────────

function RawData({ data }: { data: any }) {
  return (
    <Section title="RAW RESPONSE" defaultOpen={false}>
      <pre className="text-gray-500 font-mono text-[10px] whitespace-pre-wrap overflow-auto max-h-96 leading-relaxed">
        {JSON.stringify(data, null, 2)}
      </pre>
    </Section>
  )
}

// ── Result dispatcher ─────────────────────────────────────────────────────────

function ResultView({ data, platform }: { data: any; platform: string }) {
  const isSearch = data.target_type?.includes('search') || data.query != null
  if (isSearch) return <SearchResults data={data} />

  if (platform === 'facebook')  return <><FacebookResult  data={data} /><RawData data={data} /></>
  if (platform === 'instagram') return <><InstagramResult data={data} /><RawData data={data} /></>
  if (platform === 'tiktok')    return <><TikTokResult    data={data} /><RawData data={data} /></>
  if (platform === 'youtube')   return <><YouTubeResult   data={data} /><RawData data={data} /></>
  if (platform === 'reddit')    return <><RedditResult    data={data} /><RawData data={data} /></>
  return <RawData data={data} />
}

const PROFILE_TARGET_TYPES = new Set([
  'facebook_page', 'facebook_group', 'instagram_profile',
  'tiktok_user', 'youtube_channel', 'reddit_subreddit', 'reddit_user',
])

// ── Main component ────────────────────────────────────────────────────────────

export function SocialTargetOSINT() {
  const [urlInput, setUrlInput] = useState('')
  const [mode, setMode] = useState<'url' | 'manual'>('url')
  const [manual, setManual] = useState<ManualMode>({ platform: 'facebook', targetType: 'page', identifier: '' })
  const [searchType, setSearchType] = useState('pages')
  const [fetchState, setFetchState] = useState<FetchState>({
    status: 'idle', data: null, error: null, resolvedTarget: null,
  })
  const [reportState, setReportState] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error'
    data: ReportData | null
    error: string | null
  }>({ status: 'idle', data: null, error: null })

  const token = getToken()

  const buildEndpoint = useCallback((platform: Platform, targetType: TargetType, identifier: string): string => {
    const base = `/api/social/target/${platform}`
    const tokenPart = `token=${encodeURIComponent(token)}`

    const encode = (v: string) => encodeURIComponent(v)

    const endpointMap: Record<string, string> = {
      // Facebook
      'facebook/page':     `${base}/page?url=${encode(identifier)}&${tokenPart}`,
      'facebook/group':    `${base}/group?url=${encode(identifier)}&${tokenPart}`,
      'facebook/post':     `${base}/post?url=${encode(identifier)}&${tokenPart}`,
      'facebook/reel':     `${base}/post?url=${encode(identifier)}&${tokenPart}`,
      'facebook/event':    `${base}/event?url=${encode(identifier)}&${tokenPart}`,
      'facebook/reels':    `${base}/reels?url=${encode(identifier)}&${tokenPart}`,
      'facebook/reviews':  `${base}/reviews?url=${encode(identifier)}&${tokenPart}`,
      'facebook/search':   `${base}/search?q=${encode(identifier)}&type=${encode(searchType)}&${tokenPart}`,
      // Instagram
      'instagram/profile': `${base}/profile?username=${encode(identifier)}&${tokenPart}`,
      'instagram/media':   `${base}/media?url=${encode(identifier)}&type=post&${tokenPart}`,
      'instagram/stories': `${base}/stories?username=${encode(identifier)}&${tokenPart}`,
      'instagram/search':  `${base}/search?q=${encode(identifier)}&${tokenPart}`,
      // TikTok
      'tiktok/user':   `${base}/user?user_id=${encode(identifier)}&${tokenPart}`,
      'tiktok/video':  `${base}/video?url=${encode(identifier)}&${tokenPart}`,
      'tiktok/search': `${base}/search?q=${encode(identifier)}&type=videos&${tokenPart}`,
      // YouTube
      'youtube/channel':  `${base}/channel?id=${encode(identifier)}&${tokenPart}`,
      'youtube/video':    `${base}/video?url=${encode(identifier)}&${tokenPart}`,
      'youtube/playlist': `${base}/playlist?id=${encode(identifier)}&${tokenPart}`,
      'youtube/search':   `${base}/search?q=${encode(identifier)}&${tokenPart}`,
      // Reddit
      'reddit/subreddit': `${base}/subreddit?name=${encode(identifier)}&${tokenPart}`,
      'reddit/user':      `${base}/user?username=${encode(identifier)}&${tokenPart}`,
      'reddit/post':      `${base}/post?url=${encode(identifier)}&${tokenPart}`,
      'reddit/search':    `${base}/search?q=${encode(identifier)}&${tokenPart}`,
    }
    return endpointMap[`${platform}/${targetType}`] || `${base}/page?url=${encode(identifier)}&${tokenPart}`
  }, [token, searchType])

  const runFetch = useCallback(async (url: string, resolvedPlatform?: string, resolvedType?: string) => {
    setFetchState({ status: 'loading', data: null, error: null, resolvedTarget: null })
    try {
      const res = await fetch(url, { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      if (!res.ok) {
        const body = await res.text()
        setFetchState(s => ({ ...s, status: 'error', error: `HTTP ${res.status}: ${body.slice(0, 200)}` }))
        return
      }
      const data = await res.json()
      setFetchState({
        status: 'success', data,
        error: null,
        resolvedTarget: resolvedPlatform
          ? { platform: resolvedPlatform, target_type: resolvedType || '', identifier: '' }
          : null,
      })
    } catch (err: any) {
      setFetchState(s => ({ ...s, status: 'error', error: err.message || 'Fetch failed' }))
    }
  }, [])

  const handleUrlFetch = useCallback(async () => {
    if (!urlInput.trim()) return
    // First auto-resolve, then fetch
    const resolveUrl = `/api/social/target/resolve?url=${encodeURIComponent(urlInput.trim())}&token=${encodeURIComponent(token)}`
    setFetchState({ status: 'loading', data: null, error: null, resolvedTarget: null })
    try {
      const res = await fetch(resolveUrl, { cache: 'no-store' })
      if (!res.ok) {
        const body = await res.text()
        setFetchState(s => ({ ...s, status: 'error', error: `Could not resolve URL: ${body.slice(0, 200)}` }))
        return
      }
      const resolved = await res.json()
      const endpoint = `/api/social/target/fetch?url=${encodeURIComponent(urlInput.trim())}&token=${encodeURIComponent(token)}`
      await runFetch(endpoint, resolved.platform, resolved.target_type)
    } catch (err: any) {
      setFetchState(s => ({ ...s, status: 'error', error: err.message || 'Fetch failed' }))
    }
  }, [urlInput, token, runFetch])

  const handleManualFetch = useCallback(async () => {
    if (!manual.identifier.trim()) return
    const targetDef = PLATFORM_TARGETS[manual.platform].find(t => t.type === manual.targetType)
    if (!targetDef) return
    const endpoint = buildEndpoint(manual.platform, manual.targetType, manual.identifier.trim())
    await runFetch(endpoint, manual.platform, manual.targetType)
  }, [manual, buildEndpoint, runFetch])

  const detectedPlatform: Platform | null = (() => {
    if (!urlInput) return null
    if (urlInput.includes('facebook.com') || urlInput.includes('fb.com')) return 'facebook'
    if (urlInput.includes('instagram.com')) return 'instagram'
    if (urlInput.includes('tiktok.com')) return 'tiktok'
    if (urlInput.includes('youtube.com') || urlInput.includes('youtu.be')) return 'youtube'
    if (urlInput.includes('reddit.com')) return 'reddit'
    return null
  })()

  const activePlatform: Platform = fetchState.resolvedTarget?.platform as Platform
    || (mode === 'manual' ? manual.platform : detectedPlatform || 'facebook')

  const analyzeProfile = useCallback(async () => {
    if (!fetchState.data) return
    const identifier = fetchState.resolvedTarget?.identifier
      || (mode === 'manual' ? manual.identifier : urlInput)
    setReportState({ status: 'loading', data: null, error: null })
    try {
      const res = await fetch(`/api/social/target/profile-report?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform: activePlatform, identifier, data: fetchState.data }),
      })
      if (!res.ok) {
        const body = await res.text()
        setReportState({ status: 'error', data: null, error: `HTTP ${res.status}: ${body.slice(0, 200)}` })
        return
      }
      const reportData = await res.json()
      setReportState({ status: 'success', data: reportData, error: null })
    } catch (err: any) {
      setReportState({ status: 'error', data: null, error: err.message || 'Analysis failed' })
    }
  }, [fetchState.data, fetchState.resolvedTarget, activePlatform, mode, manual.identifier, urlInput, token])

  return (
    <div className="space-y-4">

      {/* Mode toggle */}
      <div className="flex items-center gap-1 bg-gray-900/50 border border-gray-800/50 rounded-xl p-1 w-fit">
        {(['url', 'manual'] as const).map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-4 py-1.5 rounded-lg font-mono text-[10px] font-bold tracking-widest transition-all ${
              mode === m ? 'bg-purple-800/60 text-purple-200 border border-purple-700/50' : 'text-gray-600 hover:text-gray-400'
            }`}
          >
            {m === 'url' ? 'URL / AUTO-DETECT' : 'MANUAL SELECT'}
          </button>
        ))}
      </div>

      {/* Input area */}
      <div className="bg-black/30 border border-gray-800/50 rounded-xl p-4 space-y-4">

        {mode === 'url' ? (
          /* URL mode */
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleUrlFetch()}
                  placeholder="Paste any Facebook / Instagram / TikTok / YouTube / Reddit URL..."
                  className="w-full bg-black/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white font-mono text-sm placeholder-gray-700 focus:outline-none focus:border-purple-600/50 pr-24"
                />
                {detectedPlatform && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <PlatformBadge platform={detectedPlatform} />
                  </div>
                )}
              </div>
              <button
                onClick={handleUrlFetch}
                disabled={fetchState.status === 'loading' || !urlInput.trim()}
                className="px-5 py-3 bg-purple-700/60 hover:bg-purple-700/80 disabled:bg-gray-800/50 disabled:cursor-not-allowed border border-purple-600/40 text-purple-100 font-mono text-xs font-bold rounded-xl transition-all whitespace-nowrap"
              >
                {fetchState.status === 'loading' ? 'FETCHING...' : 'FETCH'}
              </button>
            </div>
            <div className="text-gray-700 font-mono text-[10px] flex flex-wrap gap-3">
              {(['facebook', 'instagram', 'tiktok', 'youtube', 'reddit'] as Platform[]).map(p => (
                <span key={p} style={{ color: PLATFORM_META[p].color }} className="opacity-60">{PLATFORM_META[p].label}</span>
              ))}
              <span className="text-gray-700">· URLs auto-detected · paste and fetch in one click</span>
            </div>
          </div>
        ) : (
          /* Manual mode */
          <div className="space-y-3">
            {/* Platform chips */}
            <div>
              <div className="text-gray-600 font-mono text-[9px] tracking-widest mb-2">PLATFORM</div>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(PLATFORM_META) as Platform[]).map(p => (
                  <button
                    key={p}
                    onClick={() => {
                      setManual(prev => ({ ...prev, platform: p, targetType: PLATFORM_TARGETS[p][0].type, identifier: '' }))
                    }}
                    className={`px-3 py-1.5 rounded-lg font-mono text-[10px] font-bold border transition-all ${
                      manual.platform === p
                        ? `border-opacity-80 text-white`
                        : 'border-gray-800/50 text-gray-600 hover:text-gray-400 hover:border-gray-700/50'
                    }`}
                    style={manual.platform === p ? {
                      borderColor: PLATFORM_META[p].color,
                      color: PLATFORM_META[p].color,
                      background: `${PLATFORM_META[p].color}18`,
                    } : undefined}
                  >
                    {PLATFORM_META[p].label}
                  </button>
                ))}
              </div>
            </div>

            {/* Target type chips */}
            <div>
              <div className="text-gray-600 font-mono text-[9px] tracking-widest mb-2">TARGET TYPE</div>
              <div className="flex flex-wrap gap-2">
                {PLATFORM_TARGETS[manual.platform].map(t => (
                  <button
                    key={t.type}
                    onClick={() => setManual(prev => ({ ...prev, targetType: t.type, identifier: '' }))}
                    className={`px-3 py-1.5 rounded-lg font-mono text-[10px] border transition-all ${
                      manual.targetType === t.type
                        ? 'bg-gray-700/60 border-gray-500/50 text-white font-bold'
                        : 'border-gray-800/50 text-gray-600 hover:text-gray-400 hover:border-gray-700/50'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Extra: search type selector */}
            {manual.targetType === 'search' && manual.platform === 'facebook' && (
              <div>
                <div className="text-gray-600 font-mono text-[9px] tracking-widest mb-2">SEARCH TYPE</div>
                <div className="flex flex-wrap gap-2">
                  {['pages', 'people', 'posts', 'locations', 'videos'].map(st => (
                    <button key={st} onClick={() => setSearchType(st)}
                      className={`px-3 py-1 rounded font-mono text-[10px] border transition-all ${
                        searchType === st ? 'bg-blue-900/40 border-blue-700/50 text-blue-300' : 'border-gray-800/50 text-gray-600 hover:text-gray-400'
                      }`}>{st}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Identifier input */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={manual.identifier}
                onChange={e => setManual(prev => ({ ...prev, identifier: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleManualFetch()}
                placeholder={PLATFORM_TARGETS[manual.platform].find(t => t.type === manual.targetType)?.placeholder || ''}
                className="flex-1 bg-black/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white font-mono text-sm placeholder-gray-700 focus:outline-none focus:border-purple-600/50"
              />
              <button
                onClick={handleManualFetch}
                disabled={fetchState.status === 'loading' || !manual.identifier.trim()}
                className="px-5 py-3 bg-purple-700/60 hover:bg-purple-700/80 disabled:bg-gray-800/50 disabled:cursor-not-allowed border border-purple-600/40 text-purple-100 font-mono text-xs font-bold rounded-xl transition-all whitespace-nowrap"
              >
                {fetchState.status === 'loading' ? 'FETCHING...' : 'FETCH'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading */}
      {fetchState.status === 'loading' && (
        <div className="flex items-center gap-3 px-4 py-5 bg-purple-950/20 border border-purple-900/30 rounded-xl">
          <div className="w-4 h-4 rounded-full border-2 border-purple-500 border-t-transparent animate-spin shrink-0" />
          <div className="font-mono text-sm text-purple-300">Fetching intelligence — this may take a few seconds...</div>
        </div>
      )}

      {/* Error */}
      {fetchState.status === 'error' && (
        <div className="px-4 py-4 bg-red-950/30 border border-red-900/40 rounded-xl">
          <div className="text-red-400 font-mono text-xs font-bold mb-1">FETCH ERROR</div>
          <div className="text-red-300 font-mono text-xs">{fetchState.error}</div>
        </div>
      )}

      {/* Results */}
      <AnimatePresence>
        {fetchState.status === 'success' && fetchState.data && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Result header */}
            <div className="flex items-center gap-3 pb-3 border-b border-gray-800/40 flex-wrap">
              <PlatformBadge platform={activePlatform} />
              <span className="text-gray-400 font-mono text-xs">
                {fetchState.data.target_type?.replace(/_/g, ' ').toUpperCase() || 'RESULT'}
              </span>
              {PROFILE_TARGET_TYPES.has(fetchState.data.target_type) && (
                <button
                  onClick={() => {
                    setReportState({ status: 'idle', data: null, error: null })
                    analyzeProfile()
                  }}
                  disabled={reportState.status === 'loading'}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-900/40 hover:bg-green-900/60 disabled:opacity-50 disabled:cursor-not-allowed border border-green-700/40 text-green-300 font-mono text-[10px] font-bold rounded-lg transition-all"
                >
                  {reportState.status === 'loading' ? (
                    <><span className="w-2.5 h-2.5 border border-green-400 border-t-transparent rounded-full animate-spin" />ANALYSING...</>
                  ) : (
                    <>ANALYZE PROFILE</>
                  )}
                </button>
              )}
              <span className="ml-auto text-gray-700 font-mono text-[10px]">{new Date().toLocaleTimeString()}</span>
              <button
                onClick={() => {
                  setFetchState({ status: 'idle', data: null, error: null, resolvedTarget: null })
                  setReportState({ status: 'idle', data: null, error: null })
                }}
                className="text-gray-700 hover:text-gray-400 font-mono text-xs transition-colors"
              >
                × clear
              </button>
            </div>

            <ResultView data={fetchState.data} platform={activePlatform} />

            {/* Report loading */}
            {reportState.status === 'loading' && (
              <div className="flex items-center gap-3 px-4 py-5 bg-green-950/20 border border-green-900/30 rounded-xl">
                <div className="w-4 h-4 rounded-full border-2 border-green-500 border-t-transparent animate-spin shrink-0" />
                <div className="font-mono text-sm text-green-300">Running AI analysis — Grok is processing the profile data...</div>
              </div>
            )}

            {/* Report error */}
            {reportState.status === 'error' && (
              <div className="px-4 py-4 bg-red-950/30 border border-red-900/40 rounded-xl">
                <div className="text-red-400 font-mono text-xs font-bold mb-1">ANALYSIS ERROR</div>
                <div className="text-red-300 font-mono text-xs">{reportState.error}</div>
              </div>
            )}

            {/* Report result */}
            {reportState.status === 'success' && reportState.data && (
              <ProfileIntelReport
                reportData={reportState.data}
                rawData={fetchState.data}
                platform={activePlatform}
                onClose={() => setReportState({ status: 'idle', data: null, error: null })}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
